import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useEnvironment, useStartEnvironment, useStopEnvironment } from "@/queries/environment-queries";
import { environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";

type State = "running" | "starting" | "stopped" | "stopping";
const Labels: Record<State, string> = {
    stopped: "Start environment",
    starting: "Starting",
    running: "Stop environment",
    stopping: "Stopping",
};

export const EnvironmentToggle: FC<{ environmentId: string }> = ({ environmentId }) => {
    const { toast } = useToast();
    const { data: environment } = useEnvironment(environmentId);
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();

    const [state, setState] = useState<State>("stopped");

    const isGreen = state === "running" || state === "starting";
    const isTransitory = state === "starting" || state === "stopping";

    const label = Labels[state];

    useEffect(() => {
        if (!environment) {
            return;
        }
        const inProgress = environmentHasReachedStablePhase(environment);
        if (showStart(environment)) {
            setState(inProgress ? "stopped" : "stopping");
            return;
        }
        if (showStop(environment)) {
            setState(inProgress ? "running" : "starting");
            return;
        }
    }, [environment?.status?.phase, environment?.spec?.desiredPhase, environment]);

    const handleClick = useCallback(async () => {
        if (state === "stopped") {
            try {
                await startEnvironment.mutateAsync(environmentId);
            } catch (error) {
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        } else if (state === "running") {
            try {
                await stopEnvironment.mutateAsync(environmentId);
            } catch (error) {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(error),
                });
            }
        }
    }, [environmentId, startEnvironment, state, stopEnvironment, toast]);

    return (
        <div className="inline-flex h-12 w-[200px] items-center justify-end gap-2 rounded-[100px] border border-border-base bg-surface-glass py-4 pl-4 pr-3 shadow">
            <div className="grow select-none justify-start pl-1 text-base font-normal leading-tight text-content-primary">
                {label}
            </div>
            <button
                className={cn(
                    "flex h-6 w-10 items-center gap-2 rounded-xl px-0.5 py-2",
                    "transition-all duration-500 ease-in-out",
                    isGreen ? "justify-end" : "justify-start",
                    isGreen ? "bg-content-green" : "bg-surface-04",
                )}
                disabled={isTransitory}
                onClick={handleClick}
            >
                <div className="bg-surface-pure flex h-5 w-5 items-center justify-center gap-2 rounded-[10px] p-2 shadow shadow-black/10">
                    <div className="flex h-4 w-4 items-center justify-center gap-2">
                        {["starting", "stopping"].includes(state) && (
                            <IconSpinner size="sm" className="animate-spin text-orange-300" />
                        )}
                    </div>
                </div>
            </button>
        </div>
    );
};
